import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import type { CurrentUser } from '../lib/types';
import { CoinBalanceChip } from '../features/coins/CoinBalanceChip';
import { IngestorHealthBanner } from './IngestorHealthBanner';
import { AuthContext } from '../lib/auth';

export function AppShell() {
  const me = useQuery<{ user: CurrentUser }>({
    queryKey: ['me'],
    queryFn: () => api('/api/auth/me'),
    // Don't retry on 401 — that just adds latency to the
    // "you need to sign in" redirect. retry: false was here
    // before; keep it.
    retry: false,
    // First load only — TanStack Query dedups within the stale
    // window, so this query is the gating one. staleTime is
    // set at the QueryClient level (30s in main.tsx); we don't
    // override it here because the "am I authed?" check
    // changing mid-session is rare (logout does an explicit
    // invalidate). Background refetches (e.g. on window focus)
    // are fine.
  });
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  const user = me.data?.user ?? null;

  const logout = async () => {
    await api('/api/auth/logout', { method: 'POST' });
    // Drop the ENTIRE query cache, not just ['me']. Every other
    // user-scoped query (player dashboard, coins, skills, etc.) is
    // cached with no user discriminator in its key, so a stale
    // cache leaks the previous user's data to whoever signs in
    // next. qc.clear() removes all of it — no cross-user bleed.
    qc.clear();
    navigate('/login');
  };

  // Auth gate. Three states:
  //   1. me.isPending  → render a neutral loading card. This
  //      BLOCKS the header + nav from rendering until we know
  //      who the user is. The old code fell through to the
  //      header render during the pending state, which caused
  //      a "flash" — the full nav would render briefly with
  //      the unauthed "Sign in" button, then the user data
  //      would arrive and the nav would re-render. For
  //      unauthed users, the same flash happened but in
  //      reverse: full nav → "You need to sign in" card.
  //   2. me.isError    → redirect to /login. The current path
  //      is preserved as ?next= so the login page can bounce
  //      the user back to where they were trying to go. We
  //      use <Navigate> (declarative) rather than navigate()
  //      (imperative) so the redirect happens during render
  //      — no flash of the auth-failed card at all.
  //   3. me.data?.user → render the normal shell with the
  //      user state.
  //
  // The redirect's `replace: true` (via the second arg of
  // <Navigate>) means the auth-failed URL doesn't end up in
  // the browser history. Back/forward still work normally.
  if (me.isPending) {
    return <AuthLoadingCard />;
  }
  if (me.isError) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  return (
    // v0.8.9: provide AuthContext to descendant routes (SettingsPage
    // and any future self-service page). Without this, useAuth()
    // returns the createContext default (null) everywhere and the
    // SettingsPage's `if (!user) return` short-circuits to its
    // loading state forever.
    <AuthContext.Provider value={user}>
    <div className="relative z-10 min-h-full">
      <header className="border-b border-white/5 bg-bg-panel/40 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-6 px-4 py-3">
          <a href="/player" className="flex items-center gap-2 font-semibold">
            <span className="grid h-7 w-7 place-items-center rounded-md text-bg" style={{ backgroundImage: 'linear-gradient(135deg, var(--fantasy-gold) 0%, #2a1f0a 100%)' }}>M</span>
            <span>Mirabili</span>
          </a>
          <nav className="flex items-center gap-1 text-sm">
            <NavItem to="/player">Player</NavItem>
            <NavItem to="/skills">My Skills</NavItem>
            {/*
              v0.6.2: Admin nav is gated on role === 'admin'
              only. Previously it also matched 'lead', but
              every admin page uses requireRole('admin') on
              the api side, so a lead user clicking the
              Admin link got a 403 from every endpoint. That
              made the Admin menu a dead-end nav for leads.

              If the intent for the 'lead' role is "team-
              scoped admin" (the role hierarchy in the spec
              suggests this), the lead-specific endpoints
              would need to be built (e.g. /api/team/<id>/
              members, /api/team/<id>/scoring-rules, etc.)
              before adding the nav back. Until then, hiding
              the Admin link from leads is the correct
              behaviour: don't promise functionality the
              api doesn't deliver.
             */}
            {user?.role === 'admin' ? (
              <NavItem to="/admin">Admin</NavItem>
            ) : null}
            <NavItem to="/public-board" external>Public Board</NavItem>
            {/*
             * Boss Fights is hidden from the top menu while it is reworked.
             * HIDDEN, not removed: the route still exists at /boss-fights, so
             * existing links and bookmarks keep working and putting it back is
             * one line. Deliberately absent from the nav so the product does
             * not advertise a surface that is still changing shape.
             */}
            {/* <NavItem to="/boss-fights" external>Boss Fights</NavItem> */}
            <NavItem to="/quests">Quest Board</NavItem>
          </nav>
          <div className="ml-auto flex items-center gap-3 text-sm">
            {user ? (
              <>
                <CoinBalanceChip />
                {/* v0.8.9: profile chip links to /settings so the
                    player can edit their display name, handle,
                    pronouns, and public-board opt-in. Role /
                    ignore / password / email stay admin-only. */}
                <a
                  href="/settings"
                  className="text-ink-muted hover:text-ink underline-offset-2 hover:underline"
                  title="Edit your profile"
                >
                  {user.display_name}
                </a>
                <span className="rounded-full bg-bg-elevated px-2 py-0.5 text-xs uppercase tracking-wide text-ink-muted">
                  {user.role}
                </span>
                <button className="btn" onClick={logout}>Sign out</button>
              </>
            ) : (
              <a className="btn-primary" href="/login">Sign in</a>
            )}
          </div>
        </div>
      </header>
      {/* v0.8.9: admin-only ingestor failure banner. Sits directly
          under the header so it's the first thing seen after signing
          in, on every page. Renders nothing when nothing is broken. */}
      {user?.role === 'admin' ? <IngestorHealthBanner /> : null}
      <main className="mx-auto max-w-7xl px-4 py-6">
        <Outlet />
      </main>
    </div>
    </AuthContext.Provider>
  );
}

// Minimal loading card while we resolve the auth state. We
// don't want to render the full nav (which would flash the
// "Sign in" button for authed users) or the "You need to
// sign in" card (which would flash for authed users whose
// session is still being validated). A neutral branded
// loader is the right placeholder.
//
// The auth check is one round-trip to /api/auth/me, which
// on a fast connection is ~50-200ms. The loader is visible
// for that long, no more. The brand mark keeps it from
// looking like an error state.
function AuthLoadingCard() {
  return (
    <div className="grid h-full place-items-center">
      <div className="card max-w-sm text-center" role="status" aria-live="polite">
        <div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-md text-bg text-lg font-semibold" style={{ backgroundImage: 'linear-gradient(135deg, var(--fantasy-gold) 0%, #2a1f0a 100%)' }}>
          M
        </div>
        <h1 className="text-lg font-semibold">Mirabili</h1>
        <p className="mt-2 text-sm text-ink-muted">Checking your session…</p>
      </div>
    </div>
  );
}

function NavItem({ to, children, external }: { to: string; children: React.ReactNode; external?: boolean }) {
  if (external) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noreferrer"
        className="rounded-md px-3 py-1.5 text-ink-muted hover:bg-bg-elevated hover:text-ink"
      >
        {children}
      </a>
    );
  }
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'rounded-md px-3 py-1.5 hover:bg-bg-elevated hover:text-ink',
          isActive ? 'bg-bg-elevated text-ink' : 'text-ink-muted',
        )
      }
    >
      {children}
    </NavLink>
  );
}
